import { useState } from 'react';
import { AccountCircle } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, TextField } from '@mui/material';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import SimpleCard from '../../../../../components/SimpleCard';
import { registerVendorEventSuccess } from '../../../../../core/vendor-events/action';
import { validationSchema } from './validations';
import { createUpdateVendorEvent } from '../../methods';
import WizardButtons from '../WizardButtons';

export default function PointOfContact({ nextStep, initialValues, ...rest }) {
  const { vendorEventPayload, currentVendorEvent } = useSelector((store) => store.vendorEvents);
  const { user } = useSelector((store) => store.users);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Auto-fill initial values with user data if not already set
  const getInitialValues = () => {
    // If already saved in payload, use that
    if (vendorEventPayload?.vendor_event_contact?.name) {
      return vendorEventPayload.vendor_event_contact;
    }

    // Otherwise, auto-fill with user data
    if (user) {
      const userName =
        user.name && user.last_name ? `${user.name} ${user.last_name}` : user.name || '';

      // Strip +1 country code from phone number if present
      let phoneNumber = user.phone_number || '';
      if (phoneNumber.startsWith('+1')) {
        phoneNumber = phoneNumber.substring(2); // Remove "+1"
      } else if (phoneNumber.startsWith('1') && phoneNumber.length === 11) {
        phoneNumber = phoneNumber.substring(1); // Remove leading "1"
      }

      return {
        name: userName,
        phone_number: phoneNumber,
        email: user.email || '',
      };
    }

    // Fallback to initialValues or empty values
    return initialValues;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    enableReinitialize: true, // Allow form to reinitialize when user data loads
    onSubmit: (values) => {
      setLoading(true);
      const type = currentVendorEvent?.state ? 'update' : 'create';
      createUpdateVendorEvent({ ...vendorEventPayload, vendor_event_contact: values }, type)
        .then((data) => {
          dispatch(registerVendorEventSuccess(data));
          nextStep();
          setLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
        });
    },
  });

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Grid container justifyContent="center">
          <Grid item>
            <Typography fontWeight="bold">Point of Contact:</Typography>
          </Grid>
          <Grid item>
            <Typography marginLeft={2}>Who will be the vendor at this event?</Typography>
          </Grid>
        </Grid>
        <SimpleCard variant="outlined" sx={{ display: 'flex' }}>
          <Grid container columns={10} justifyContent={{ xs: 'center', md: 'left' }}>
            <Grid item xs={2} textAlign="center">
              <AccountCircle style={{ fontSize: '5rem' }} />
            </Grid>
            <Grid item>
              <TextField
                style={{ marginBottom: '1rem' }}
                fullWidth
                label="Name"
                name="name"
                value={formik.values.name || ''}
                onChange={formik.handleChange}
                variant="standard"
                size="small"
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                style={{ marginBottom: '1rem' }}
                fullWidth
                label="Phone Number"
                name="phone_number"
                inputProps={{ maxLength: 10 }}
                value={formik.values.phone_number || ''}
                onChange={formik.handleChange}
                variant="standard"
                size="small"
                error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
                helperText={formik.touched.phone_number && formik.errors.phone_number}
              />
              <TextField
                style={{ marginBottom: '1rem' }}
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formik.values.email || ''}
                onChange={formik.handleChange}
                variant="standard"
                size="small"
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
          </Grid>
        </SimpleCard>
        <WizardButtons nextLabelButtonType="submit" loading={loading} {...rest} />
      </form>
    </div>
  );
}

PointOfContact.propTypes = {
  nextStep: PropTypes.func,
  initialValues: PropTypes.instanceOf(Object),
};

PointOfContact.defaultProps = {
  nextStep: () => {},
  initialValues: {
    name: '',
    email: '',
    phone_number: '',
  },
};
